import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import { Button } from './ui/button';
import BugReportButton from './BugReportButton';
import ForcePasswordChange from './ForcePasswordChange';
import { bugApi } from '../lib/api';
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  Settings,
  LogOut,
  Menu,
  X,
  Building2,
  ChevronRight,
  Calendar,
  UsersRound,
  HardHat,
  CreditCard,
  Bug,
  Receipt,
  ReceiptText,
  Clock,
  TrendingUp,
  Calculator,
} from 'lucide-react';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/leads', label: 'Leads', icon: Users },
  { path: '/projects', label: 'Projects', icon: FolderKanban },
  { path: '/clients', label: 'Clients', icon: Building2 },
  { path: '/calendar', label: 'Calendar', icon: Calendar },
  { path: '/reports', label: 'Reports', icon: TrendingUp },
  { type: 'separator', label: 'Financials' },
  { path: '/invoices', label: 'Invoices', icon: Receipt },
  { path: '/credit-memos', label: 'Credit Memos', icon: ReceiptText },
  { path: '/time-tracking', label: 'Time Tracking', icon: Clock },
  { path: '/accounting', label: 'Tax & Accounting', icon: Calculator },
  { path: '/billing', label: 'Billing', icon: CreditCard },
  { type: 'separator', label: 'Team' },
  { path: '/team', label: 'Team Members', icon: UsersRound },
  { path: '/subcontractors', label: 'Subcontractors', icon: HardHat },
  { type: 'separator' },
  { path: '/settings', label: 'Settings', icon: Settings },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { darkMode } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [bugReportEnabled, setBugReportEnabled] = useState(false);
  const [showBugDialog, setShowBugDialog] = useState(false);
  
  // Get custom logo from user branding
  const customLogo = user?.branding?.custom_logo;
  const companyName = user?.branding?.company_name || 'Corbel';

  // Check if bug reporting is enabled
  useEffect(() => {
    const checkBugReporting = async () => {
      try {
        const response = await bugApi.getSettings();
        setBugReportEnabled(response.data.enabled);
      } catch (error) {
        setBugReportEnabled(false);
      }
    };
    checkBugReporting();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className={cn("min-h-screen flex transition-colors duration-200", darkMode ? "bg-slate-900" : "bg-background")}>
      {/* Force password change modal for beta users */}
      <ForcePasswordChange />
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed lg:static inset-y-0 left-0 z-50 w-64 border-r transform transition-all duration-200 lg:transform-none',
          darkMode ? 'bg-slate-800 border-slate-700' : 'bg-card border-border',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className={cn("h-16 flex items-center justify-between px-4 border-b", darkMode ? "border-slate-700" : "border-border")}>
            <Link to="/dashboard" className="flex items-center gap-2">
              {customLogo ? (
                <img 
                  src={customLogo} 
                  alt={companyName} 
                  className="h-8 max-w-[140px] object-contain"
                  data-testid="custom-logo"
                />
              ) : (
                <>
                  <Building2 className="h-8 w-8 text-[var(--brand-primary,#f97316)]" strokeWidth={1.5} />
                  <span className={cn("font-bold text-lg tracking-tight", darkMode && "text-white")}>{companyName}</span>
                </>
              )}
            </Link>
            <button
              className={cn("lg:hidden p-2 rounded-sm", darkMode ? "hover:bg-slate-700" : "hover:bg-secondary")}
              onClick={() => setSidebarOpen(false)}
            >
              <X className={cn("h-5 w-5", darkMode && "text-slate-300")} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item, index) => {
              // Handle separators
              if (item.type === 'separator') {
                return (
                  <div key={`sep-${index}`} className="pt-3 pb-1">
                    {item.label && (
                      <p className={cn(
                        "px-3 text-xs font-semibold uppercase tracking-wider",
                        darkMode ? "text-slate-500" : "text-muted-foreground/60"
                      )}>
                        {item.label}
                      </p>
                    )}
                    {!item.label && <div className={cn("border-t mx-3", darkMode ? "border-slate-700" : "border-border")} />}
                  </div>
                );
              }

              const Icon = item.icon;
              const isActive = location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-sm font-medium transition-colors',
                    isActive
                      ? 'bg-orange-500 text-white'
                      : darkMode 
                        ? 'text-slate-300 hover:bg-slate-700 hover:text-white'
                        : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  )}
                  onClick={() => setSidebarOpen(false)}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* User section */}
          <div className={cn("p-4 border-t", darkMode ? "border-slate-700" : "border-border")}>
            <div className="flex items-center gap-3 mb-3 px-2">
              <div className={cn("h-10 w-10 rounded-sm flex items-center justify-center text-white font-semibold", darkMode ? "bg-slate-600" : "bg-slate-900")}>
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn("font-medium text-sm truncate", darkMode && "text-white")}>{user?.name}</p>
                <p className={cn("text-xs truncate", darkMode ? "text-slate-400" : "text-muted-foreground")}>{user?.company || 'No company'}</p>
              </div>
            </div>
            {bugReportEnabled && (
              <Button
                variant="outline"
                className={cn("w-full justify-start gap-2 rounded-sm mb-2 border-red-600/50 text-red-500 hover:bg-red-600/10 hover:text-red-400", darkMode && "border-red-600/50")}
                onClick={() => setShowBugDialog(true)}
                data-testid="sidebar-bug-report-btn"
              >
                <Bug className="h-4 w-4" strokeWidth={1.5} />
                <span>Report a Bug</span>
              </Button>
            )}
            <Button
              variant="outline"
              className={cn("w-full justify-start gap-2 rounded-sm", darkMode && "border-slate-600 text-slate-300 hover:bg-slate-700 hover:text-white")}
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" strokeWidth={1.5} />
              <span>Log out</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <header className={cn("h-16 border-b flex items-center justify-between px-4 lg:px-6", darkMode ? "bg-slate-800 border-slate-700" : "bg-card border-border")}>
          <button
            className={cn("lg:hidden p-2 rounded-sm", darkMode ? "hover:bg-slate-700" : "hover:bg-secondary")}
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className={cn("h-5 w-5", darkMode && "text-slate-300")} />
          </button>
          
          <div className="flex-1" />
          
          <div className="flex items-center gap-4">
            <span className={cn("text-sm hidden sm:block", darkMode ? "text-slate-400" : "text-muted-foreground")}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </span>
          </div>
        </header>

        {/* Page content */}
        <main className={cn("flex-1 overflow-auto p-4 lg:p-6", darkMode && "bg-slate-900")}>
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
      
      {/* Floating Bug Report Button */}
      <BugReportButton externalOpen={showBugDialog} onExternalClose={() => setShowBugDialog(false)} />
    </div>
  );
}
